/**
 * TanStack Query client factory (epic #2266, phase 1 — foundation).
 *
 * Encodes the behavior the bespoke `useCache` layer provides today so that as
 * consumers migrate they inherit the same semantics rather than regress:
 *  - `refetchOnWindowFocus` + `staleTime` (focus refetch only when stale)
 *  - **`refetchIntervalInBackground: false`** — the key win: `refetchInterval`
 *    pollers go silent in hidden tabs, cutting Azure API/Cosmos cost.
 *  - classification-aware `retry` + exponential-backoff `retryDelay`
 *  - a per-key circuit breaker fed from the query cache's error/success events
 *  - error/exception telemetry to Application Insights
 *
 * The provider is intentionally inert until hooks adopt it (later phases).
 */

import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query';
import appInsightsService from '../../services/appInsightsService.js';
import Logger from '../../utils/logger.js';
import { shouldRetry, backoffDelay, classifyError } from './errorClassification.js';
import { circuitBreaker } from './circuitBreaker.js';

// Default staleness: 60s. Individual queries override per data type during
// migration (e.g. static reference data 24h, realtime 30s).
const DEFAULT_STALE_TIME_MS = 60 * 1000;
const DEFAULT_GC_TIME_MS = 5 * 60 * 1000;

/** Stable string key for a query, used to key the circuit breaker. */
function keyOf(query) {
  // TanStack sets a stable queryHash on every real query; the fallback only runs
  // for hand-built query stubs and is guarded against un-stringifiable keys.
  if (query?.queryHash) return query.queryHash;
  try {
    return JSON.stringify(query?.queryKey ?? []);
  } catch {
    return String(query?.queryKey ?? '');
  }
}

/** Feed the circuit breaker, log, and report the exception to telemetry. */
function handleQueryError(error, query) {
  const classification = classifyError(error);
  circuitBreaker.recordFailure(keyOf(query), error, classification);
  Logger.warn('[queryClient] query error', {
    queryHash: query?.queryHash,
    type: classification.type,
    retryable: classification.retryable
  });
  appInsightsService.trackException(error, {
    source: 'react-query',
    queryHash: query?.queryHash,
    classification: classification.type
  });
}

/** Reset the circuit breaker for a query that succeeded. */
function handleQuerySuccess(_data, query) {
  circuitBreaker.recordSuccess(keyOf(query));
}

/**
 * Create a configured QueryClient. A factory (not a singleton export) so tests
 * get an isolated client and the breaker/telemetry wiring is explicit.
 * @param {object} [overrides] - Shallow-merged overrides for defaultOptions.queries
 * @returns {QueryClient}
 */
export function createQueryClient(overrides = {}) {
  const queryCache = new QueryCache({
    onError: handleQueryError,
    onSuccess: handleQuerySuccess
  });

  const mutationCache = new MutationCache({
    onError: error => appInsightsService.trackException(error, { source: 'react-query-mutation' })
  });

  return new QueryClient({
    queryCache,
    mutationCache,
    defaultOptions: {
      queries: {
        staleTime: DEFAULT_STALE_TIME_MS,
        gcTime: DEFAULT_GC_TIME_MS,
        refetchOnWindowFocus: true,
        // The cost win: do not poll in background/hidden tabs.
        refetchIntervalInBackground: false,
        retry: (failureCount, error) => shouldRetry(failureCount, error),
        retryDelay: attemptIndex => backoffDelay(attemptIndex),
        ...overrides
      },
      mutations: {
        retry: false
      }
    }
  });
}

/** App-wide singleton client. */
export const queryClient = createQueryClient();
